import { Component, computed, inject, input } from '@angular/core';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { PixlEditorComponent } from '@wdidev/pixl-editor';
import { FreeInputTextItem, FreeInputTextItemConfig, FreeInputTextPart, PlayerSettings } from '../../../core/types/player-types';
import { LearnItemConfig, NlpOptions } from '../../exercise/exercise.entity';
import { ContentEditorService } from '../../../core/services/content-editor.service';
import TurndownService from 'turndown';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'pixl-free-input-text-editor',
  standalone: true,
  imports: [
    CdkDrag,
    CdkDragHandle,
    CdkDropList,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    PixlEditorComponent,
    TranslocoModule,
    MatTooltip,
  ],
  templateUrl: './free-input-text-editor.component.html',
  styleUrl: './free-input-text-editor.component.scss',
})
export class FreeInputTextEditorComponent {
  public readonly part = input.required<FreeInputTextPart>();
  public readonly exerciseConfig = input.required<LearnItemConfig>();
  public readonly kiService = input<boolean>(false);
  public readonly authToken = input<string | null>(null);
  public readonly assetsUrl = input<string>('');
  public readonly mediaUrl = input<string>('');
  public readonly playerSettings = input<PlayerSettings | null>(null);

  public expandedItems = new Set<string>();
  public expandedKiOptions = new Set<string>();

  private readonly contentEditorService = inject(ContentEditorService);
  private turndownService = new TurndownService();

  public get materialTabName(): string | undefined {
    return this.exerciseConfig().materialTabName;
  }

  public set materialTabName(value: string) {
    this.exerciseConfig().materialTabName = value || undefined;
    this.contentEditorService.notifyPartChanged();
  }

  public readonly lastNlpResult = computed(() => this.exerciseConfig().lastNLPResult ?? null);

  public readonly nlpOptions = computed(() => this.lastNlpResult()?.options ?? {});

  public readonly componentKeys = computed(() => Object.keys(this.nlpOptions().enabled_components ?? {}));

  public readonly categoryKeys = computed(() => Object.keys(this.nlpOptions().enabled_categories ?? {}));

  public readonly ignoredCodes = computed(() => this.nlpOptions().ignore_feedback ?? []);

  public readonly availableCodesToAdd = computed(() => {
    const allKeys = [...this.componentKeys(), ...this.categoryKeys()];
    return allKeys.filter(k => !this.ignoredCodes().includes(k));
  });

  public readonly contentMatchingBasedOn = computed(() => this.nlpOptions().content_matching_based_on ?? 'lemma');

  public readonly useExerciseTextAsLexicon = computed(() => this.nlpOptions().use_exercise_text_as_lexicon ?? false);

  public isComponentEnabled(key: string): boolean {
    return this.nlpOptions().enabled_components?.[key] ?? false;
  }

  public isCategoryEnabled(key: string): boolean {
    return this.nlpOptions().enabled_categories?.[key] ?? false;
  }

  public toggleComponent(key: string, enabled: boolean): void {
    const components = { ...(this.nlpOptions().enabled_components ?? {}) };
    components[key] = enabled;
    this.updateNlpOptions({ enabled_components: components });
  }

  public toggleCategory(key: string, enabled: boolean): void {
    const categories = { ...(this.nlpOptions().enabled_categories ?? {}) };
    categories[key] = enabled;
    this.updateNlpOptions({ enabled_categories: categories });
  }

  public addIgnoreCode(code: string): void {
    this.updateNlpOptions({ ignore_feedback: [...this.ignoredCodes(), code] });
  }

  public removeIgnoreCode(code: string): void {
    const codes = this.ignoredCodes().filter(c => c !== code);
    this.updateNlpOptions({ ignore_feedback: codes.length > 0 ? codes : undefined });
  }

  public setContentMatchingBasedOn(value: 'lemma' | 'token'): void {
    this.updateNlpOptions({ content_matching_based_on: value });
  }

  public setUseExerciseTextAsLexicon(value: boolean): void {
    this.updateNlpOptions({ use_exercise_text_as_lexicon: value || undefined });
  }

  public onMarkupChange(markup: string): void {
    this.part().markup = markup;
    this.part().markdown = this.turndownService.turndown(markup);
    this.contentEditorService.notifyPartChanged();
  }

  public collapseAll(): void {
    this.expandedItems.clear();
  }

  public expandAll(): void {
    this.part().items.forEach(item => this.expandedItems.add(item.id));
  }

  public toggleItem(id: string): void {
    if (this.expandedItems.has(id)) {
      this.expandedItems.delete(id);
    }
    else {
      this.expandedItems.add(id);
    }
  }

  public toggleKiOptions(id: string): void {
    if (this.expandedKiOptions.has(id)) {
      this.expandedKiOptions.delete(id);
    }
    else {
      this.expandedKiOptions.add(id);
    }
  }

  public addItem(): void {
    this.part().items = [
      ...this.part().items,
      {
        id: crypto.randomUUID(),
        kind: 'plainText',
        text: '',
      },
    ];
    this.contentEditorService.notifyPartChanged();
  }

  public removeItem(index: number): void {
    const items = [...this.part().items];
    const removed = items.splice(index, 1);
    if (removed[0]) {
      this.expandedItems.delete(removed[0].id);
      this.expandedKiOptions.delete(removed[0].id);
    }
    this.part().items = items;
    this.contentEditorService.notifyPartChanged();
  }

  public onDrop(event: CdkDragDrop<FreeInputTextItem[]>): void {
    const items = [...this.part().items];
    moveItemInArray(items, event.previousIndex, event.currentIndex);
    this.part().items = items;
    this.contentEditorService.notifyPartChanged();
  }

  public isInputField(item: FreeInputTextItem): boolean {
    return item.kind === 'freeInputText';
  }

  public setInputField(item: FreeInputTextItem, checked: boolean): void {
    this.replaceItem(item, { kind: checked ? 'freeInputText' : 'plainText' });
  }

  public onItemTextChange(item: FreeInputTextItem, value: string): void {
    this.replaceItem(item, { text: value });
  }

  public onItemHintChange(item: FreeInputTextItem, value: string): void {
    this.replaceItem(item, { hint: value || undefined });
  }

  public onItemPerfectSolutionChange(item: FreeInputTextItem, value: string): void {
    this.replaceItem(item, { perfectSolution: value || undefined });
  }

  public isNlpDisabled(item: FreeInputTextItem): boolean {
    return item.config?.nlpDisabled ?? false;
  }

  public setNlpDisabled(item: FreeInputTextItem, checked: boolean): void {
    if (checked) {
      this.replaceItem(item, { config: { ...item.config, nlpDisabled: true } });
    }
    else {
      this.cleanItemConfig(item, 'nlpDisabled');
    }
  }

  public getNlpThreshold(item: FreeInputTextItem): number | '' {
    return item.config?.nlpFeedbackCorrectnessThreshold ?? '';
  }

  public setNlpThreshold(item: FreeInputTextItem, value: number | string): void {
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (!isNaN(num)) {
      this.replaceItem(item, { config: { ...item.config, nlpFeedbackCorrectnessThreshold: num } });
    }
    else {
      this.cleanItemConfig(item, 'nlpFeedbackCorrectnessThreshold');
    }
  }

  public isNlpContext(item: FreeInputTextItem): boolean {
    return item.config?.nlpContext ?? false;
  }

  public setNlpContext(item: FreeInputTextItem, checked: boolean): void {
    if (checked) {
      this.replaceItem(item, { config: { ...item.config, nlpContext: true } });
    }
    else {
      this.cleanItemConfig(item, 'nlpContext');
    }
  }

  private updateNlpOptions(update: Partial<NlpOptions>): void {
    const current = this.exerciseConfig().lastNLPResult ?? {};
    this.exerciseConfig().lastNLPResult = {
      ...current,
      options: { ...(current.options ?? {}), ...update },
    };
    this.contentEditorService.notifyPartChanged();
  }

  private replaceItem(item: FreeInputTextItem, patch: Partial<FreeInputTextItem>): void {
    this.part().items = this.part().items.map(i => i.id === item.id ? { ...i, ...patch } : i);
    this.contentEditorService.notifyPartChanged();
  }

  private cleanItemConfig(item: FreeInputTextItem, key: keyof FreeInputTextItemConfig): void {
    if (!item.config) {
      return;
    }
    const config: FreeInputTextItemConfig = { ...item.config };
    delete config[key];
    this.replaceItem(item, { config: Object.keys(config).length > 0 ? config : null });
  }
}
